import PracticeSettingsService from './PracticeSettingsService';

export const PRACTICE_PLAYER_MODES = {
  FOCUS: 'focus',
  DRILL: 'drill',
  REVIEW: 'review',
};

export const PRACTICE_PLAYER_MAX_CARDS = 100;
export const PRACTICE_PLAYER_PROGRESS_JITTER = 0.15;

export function normalizePracticePlayerMode(value) {
  return Object.values(PRACTICE_PLAYER_MODES).includes(value)
    ? value
    : PRACTICE_PLAYER_MODES.FOCUS;
}

export function isPracticePlayerCompletedCard(card) {
  return PracticeSettingsService.isGraduated(card);
}

export function getPracticePlayerRepeatTarget(mode, card, { isQuickPractice = false } = {}) {
  if (PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING) {
    return 1;
  }
  return isQuickPractice ? 2 : 4;
}

export function getPracticePlayerStepCount(mode, card, options) {
  if (PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING) {
    return 2;
  }
  return 3 + (getPracticePlayerRepeatTarget(mode, card, options) * 2);
}

export function shuffleCards(cards, random = Math.random) {
  const result = [...cards];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

export function weightedFocusOrder(cards, random = Math.random) {
  const remaining = [...cards];
  const ordered = [];
  while (remaining.length > 0) {
    const weights = remaining.map((card) => 1 / (PracticeSettingsService.getCardPracticeCount(card) + 1));
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let target = random() * totalWeight;
    let selectedIndex = weights.length - 1;
    for (let index = 0; index < weights.length; index += 1) {
      target -= weights[index];
      if (target <= 0) {
        selectedIndex = index;
        break;
      }
    }
    ordered.push(remaining.splice(selectedIndex, 1)[0]);
  }
  return ordered;
}

export function buildListeningOrder(
  cards,
  random = Math.random,
  maxCards = PRACTICE_PLAYER_MAX_CARDS
) {
  const ranked = (cards || [])
    .map((card, index) => ({
      card,
      index,
      priority: PracticeSettingsService.getListeningProgress(card) +
        (random() * PRACTICE_PLAYER_PROGRESS_JITTER),
    }))
    .sort((a, b) => {
      const priorityDifference = a.priority - b.priority;
      return priorityDifference !== 0 ? priorityDifference : a.index - b.index;
    })
    .slice(0, maxCards)
    .map((item) => item.card);

  return shuffleCards(ranked, random);
}

export function buildPracticePlayerPlaylist(cards = [], {
  mode,
  settings = {},
  random = Math.random,
  includeAllCards = false,
} = {}) {
  if (includeAllCards) {
    return shuffleCards((cards || []).filter(Boolean), random);
  }

  const normalizedMode = normalizePracticePlayerMode(mode);
  const eligible = (cards || []).filter((card) => {
    if (!PracticeSettingsService.matchesPracticeCardTypeFilter(card, settings.practiceCardTypeFilter)) {
      return false;
    }

    const completed = isPracticePlayerCompletedCard(card);
    if (normalizedMode === PRACTICE_PLAYER_MODES.REVIEW) return completed;

    return !completed && !PracticeSettingsService.isAssessmentGradeFour(card);
  });

  if (normalizedMode === PRACTICE_PLAYER_MODES.REVIEW) {
    return shuffleCards(eligible, random).slice(0, PRACTICE_PLAYER_MAX_CARDS);
  }

  return buildListeningOrder(eligible, random);
}

const PracticePlayerService = {
  buildPracticePlayerPlaylist,
  buildListeningOrder,
  getPracticePlayerRepeatTarget,
  getPracticePlayerStepCount,
  isPracticePlayerCompletedCard,
  normalizePracticePlayerMode,
  PRACTICE_PLAYER_MAX_CARDS,
  PRACTICE_PLAYER_PROGRESS_JITTER,
  PRACTICE_PLAYER_MODES,
  shuffleCards,
  weightedFocusOrder,
};

export default PracticePlayerService;
